import apiClient from './client';
import type { PaginatedResponse } from '@/types/api';
import type {
  AdminUser,
  AdminUserFormData,
  AdminUserFilters,
  AuditLogEntry,
  AuditLogFilters,
  UserScopesPayload,
} from '@/types/admin';

/* === Users === */

export async function getUsers(
  params?: AdminUserFilters,
): Promise<PaginatedResponse<AdminUser>> {
  const response = await apiClient.get<PaginatedResponse<AdminUser>>(
    '/users/',
    { params },
  );
  return response.data;
}

export async function getUser(id: string): Promise<AdminUser> {
  const response = await apiClient.get<AdminUser>(`/users/${id}/`);
  return response.data;
}

export async function createUser(data: AdminUserFormData): Promise<AdminUser> {
  const payload = {
    name: data.name,
    email: data.email,
    password: data.password || undefined,
    member_id: data.member_id || undefined,
    is_active: data.is_active ?? true,
    roles: data.role ? [data.role] : [],
  };
  const response = await apiClient.post<AdminUser>('/users/', payload);
  return response.data;
}

export async function updateUser(
  id: string,
  data: Partial<AdminUserFormData>,
): Promise<AdminUser> {
  const response = await apiClient.put<AdminUser>(`/users/${id}/`, data);
  return response.data;
}

export async function toggleUserActive(id: string): Promise<AdminUser> {
  const response = await apiClient.post<{ data: AdminUser }>(`/users/${id}/toggle-active`);
  return response.data.data;
}

export async function resetUserPassword(id: string): Promise<{ message: string; temporary_password: string }> {
  const response = await apiClient.post<{ message: string; temporary_password: string }>(`/users/${id}/reset-password`);
  return response.data;
}

export async function updateUserRoles(id: string, roles: string[]): Promise<AdminUser> {
  const response = await apiClient.put<{ data: AdminUser }>(`/users/${id}/roles`, { roles });
  return response.data.data;
}

export async function getUserScopes(id: string): Promise<UserScopesPayload> {
  const response = await apiClient.get<{ data: UserScopesPayload }>(`/users/${id}/scopes`);
  return response.data.data;
}

export async function updateUserScopes(
  id: string,
  scopes: { group_ids?: string[]; sector_ids?: string[] },
): Promise<Partial<UserScopesPayload>> {
  const response = await apiClient.put<{ data: Partial<UserScopesPayload> }>(`/users/${id}/scopes`, scopes);
  return response.data.data;
}

/* === Audit Log === */

export async function getAuditLog(
  params?: AuditLogFilters,
): Promise<PaginatedResponse<AuditLogEntry>> {
  const response = await apiClient.get<PaginatedResponse<AuditLogEntry>>(
    '/audit-logs/',
    { params },
  );
  return response.data;
}

export async function getAuditLogEntry(id: string): Promise<AuditLogEntry> {
  const response = await apiClient.get<AuditLogEntry>(`/audit-logs/${id}/`);
  return response.data;
}

export async function getAuditActions(): Promise<string[]> {
  const response = await apiClient.get<{ data: string[] }>('/audit-logs/actions');
  return response.data.data;
}

export async function exportAuditLog(params?: AuditLogFilters): Promise<Blob> {
  const response = await apiClient.get('/audit-logs/export', {
    params,
    responseType: 'blob',
  });
  return response.data;
}
